import { create } from 'zustand';
import { getRandomScenario, formatPrice } from './scenarios';
import {
  calculateOpponentAction, handleBluff, handlePressure,
  getRandomPersonality, getPersonality, getOpponentName,
} from './opponents';
import { calculateScore, getStreakBonus } from './scoring';

const INITIAL_STATE = {
  screen: 'menu',  // menu | playing | results | career
  difficulty: 'medium',

  // Current negotiation
  scenario: null,
  personality: null,
  personalityData: null,
  opponentName: '',
  playerRole: '', // buyer | seller

  // Offers
  currentOffer: 0,
  opponentLastOffer: null,
  playerLastOffer: null,

  // State
  round: 0,
  timeRemaining: 0,
  timeLimit: 0,
  timerActive: false,
  tensionLevel: 0,        // 0.0 - 1.0
  opponentTrust: 1.0,     // 1.0 = full trust, decreases with failed bluffs
  opponentMood: 'neutral', // neutral, pleased, annoyed, angry, nervous, etc.

  // Counters
  playerBluffCount: 0,
  bluffSuccesses: 0,
  pressuresUsed: 0,

  // Dialog
  chatLog: [],
  opponentTyping: false,

  // Result
  dealReached: false,
  finalPrice: null,
  lastScore: null,

  // Actions available
  playerCanAct: false,
  roundProcessing: false,

  // Career
  careerScore: 0,
  dealsCompleted: 0,
  dealsWalked: 0,
  winStreak: 0,
  bestStreak: 0,
  totalRoundsPlayed: 0,
  scenariosPlayed: [],
  careerHistory: [],
};

const useGameStore = create((set, get) => ({
  ...INITIAL_STATE,

  // ─── MENU ───
  setDifficulty: (d) => set({ difficulty: d }),
  goToMenu: () => set({ screen: 'menu' }),
  goToCareer: () => set({ screen: 'career' }),

  // ─── START GAME ───
  startNegotiation: (scenarioOverride) => {
    const state = get();
    const scenario = scenarioOverride || getRandomScenario(state.scenariosPlayed.slice(-3));
    const personality = getRandomPersonality();
    const pData = getPersonality(personality);
    const opName = getOpponentName(personality);

    const initialOffer = scenario.listPrice;

    set({
      screen: 'playing',
      scenario,
      personality,
      personalityData: pData,
      opponentName: opName,
      playerRole: scenario.playerRole,
      currentOffer: initialOffer,
      opponentLastOffer: initialOffer,
      playerLastOffer: null,
      round: 0,
      timeRemaining: scenario.timeLimit,
      timeLimit: scenario.timeLimit,
      timerActive: true,
      tensionLevel: 0,
      opponentTrust: 1.0,
      opponentMood: pData.initialReaction,
      playerBluffCount: 0,
      bluffSuccesses: 0,
      pressuresUsed: 0,
      chatLog: [
        {
          sender: 'system',
          text: `📋 ${scenario.title} — ${scenario.description}`,
          type: 'scenario',
        },
        {
          sender: 'system',
          text: `💡 ${scenario.context}`,
          type: 'info',
        },
        {
          sender: 'system',
          text: scenario.playerRole === 'buyer'
            ? `You want to BUY at or below ${formatPrice(scenario.playerTarget, scenario)}. Listed at ${formatPrice(scenario.listPrice, scenario)}.`
            : `You want to SELL at or above ${formatPrice(scenario.playerTarget, scenario)}. Their offer: ${formatPrice(scenario.listPrice, scenario)}.`,
          type: 'objective',
        },
        {
          sender: 'opponent',
          text: pData.dialogPatterns.greeting[Math.floor(Math.random() * pData.dialogPatterns.greeting.length)],
          emotion: pData.initialReaction,
        },
      ],
      dealReached: false,
      finalPrice: null,
      lastScore: null,
      playerCanAct: true,
      roundProcessing: false,
    });
  },

  // ─── TIMER TICK ───
  tick: () => {
    const state = get();
    if (!state.timerActive || state.timeRemaining <= 0) return;

    const newTime = state.timeRemaining - 1;
    const timePercent = newTime / state.timeLimit;

    // Tension increases as time decreases
    let newTension = state.tensionLevel;
    if (timePercent < 0.5) newTension = Math.min(1, newTension + 0.008);
    if (timePercent < 0.25) newTension = Math.min(1, newTension + 0.015);

    // Time-based opponent warning
    let extraChat = null;
    if (newTime === Math.floor(state.timeLimit * 0.25)) {
      const p = state.personalityData;
      const lines = p.dialogPatterns.timeWarning;
      extraChat = {
        sender: 'opponent',
        text: lines[Math.floor(Math.random() * lines.length)],
        emotion: 'urgent',
      };
    }

    if (newTime <= 0) {
      // Time's up — force a result
      get().endNegotiation(false, false, true);
      return;
    }

    set(s => ({
      timeRemaining: newTime,
      tensionLevel: newTension,
      chatLog: extraChat ? [...s.chatLog, extraChat] : s.chatLog,
    }));
  },

  // ─── PLAYER ACTIONS ───
  submitOffer: (price) => {
    const state = get();
    if (!state.playerCanAct || state.roundProcessing) return;

    set({
      playerCanAct: false,
      roundProcessing: true,
      playerLastOffer: price,
      currentOffer: price,
      round: state.round + 1,
      tensionLevel: Math.min(1, state.tensionLevel + 0.03),
      chatLog: [
        ...state.chatLog,
        {
          sender: 'player',
          text: `I'll ${state.playerRole === 'buyer' ? 'offer' : 'accept no less than'} ${formatPrice(price, state.scenario)}.`,
          type: 'offer',
        },
      ],
    });

    // Opponent thinks...
    setTimeout(() => {
      set({ opponentTyping: true });
    }, 300);

    setTimeout(() => {
      const s = get();
      const result = calculateOpponentAction({
        personality: s.personality,
        scenario: s.scenario,
        currentOffer: price,
        opponentLastOffer: s.opponentLastOffer,
        round: s.round,
        timeRemaining: s.timeRemaining,
        timeLimit: s.timeLimit,
        playerLastAction: 'offer',
        playerBluffCount: s.playerBluffCount,
        tensionLevel: s.tensionLevel,
      });

      if (result.action === 'accept') {
        set(prev => ({
          chatLog: [...prev.chatLog, { sender: 'opponent', text: result.message, emotion: result.emotion }],
          opponentTyping: false,
          opponentMood: result.emotion,
        }));
        setTimeout(() => get().endNegotiation(true, false, false, price), 800);
        return;
      }

      if (result.action === 'walkaway') {
        set(prev => ({
          chatLog: [...prev.chatLog, { sender: 'opponent', text: result.message, emotion: result.emotion }],
          opponentTyping: false,
          opponentMood: 'angry',
        }));
        setTimeout(() => get().endNegotiation(false, true, false), 800);
        return;
      }

      // Counter or reject
      set(prev => ({
        opponentLastOffer: result.newPrice || prev.opponentLastOffer,
        currentOffer: result.newPrice || prev.currentOffer,
        opponentMood: result.emotion,
        opponentTyping: false,
        playerCanAct: true,
        roundProcessing: false,
        chatLog: [
          ...prev.chatLog,
          { sender: 'opponent', text: result.message, emotion: result.emotion },
        ],
      }));
    }, 1200 + Math.random() * 1000);
  },

  // ─── BLUFF ───
  doBluff: () => {
    const state = get();
    if (!state.playerCanAct || state.roundProcessing) return;

    set({
      playerCanAct: false,
      roundProcessing: true,
      playerBluffCount: state.playerBluffCount + 1,
      tensionLevel: Math.min(1, state.tensionLevel + 0.08),
      chatLog: [
        ...state.chatLog,
        {
          sender: 'player',
          text: state.playerRole === 'buyer'
            ? "This is my final offer. I have another deal lined up."
            : "I've got someone else offering more. This is your last chance.",
          type: 'bluff',
        },
      ],
    });

    setTimeout(() => set({ opponentTyping: true }), 300);

    setTimeout(() => {
      const s = get();
      const result = handleBluff({
        personality: s.personality,
        playerBluffCount: s.playerBluffCount,
        tensionLevel: s.tensionLevel,
        round: s.round,
      });

      if (result.success) {
        // Apply concession
        const scenario = s.scenario;
        const isBuyer = scenario.playerRole === 'buyer';
        const current = s.opponentLastOffer || scenario.opponentTarget;
        const concession = Math.abs(current - scenario.opponentFloor) * result.concessionBonus;
        const newPrice = isBuyer ? current - concession : current + concession;
        const rounded = roundPriceForScenario(newPrice, scenario);

        set(prev => ({
          opponentLastOffer: rounded,
          currentOffer: rounded,
          bluffSuccesses: prev.bluffSuccesses + 1,
          opponentMood: result.emotion,
          opponentTyping: false,
          playerCanAct: true,
          roundProcessing: false,
          chatLog: [
            ...prev.chatLog,
            {
              sender: 'opponent',
              text: result.message.replace('{price}', formatPrice(rounded, scenario)),
              emotion: result.emotion,
            },
          ],
        }));
      } else {
        set(prev => ({
          opponentTrust: Math.max(0.2, prev.opponentTrust - result.trustPenalty),
          opponentMood: result.emotion,
          opponentTyping: false,
          playerCanAct: true,
          roundProcessing: false,
          chatLog: [
            ...prev.chatLog,
            { sender: 'opponent', text: result.message, emotion: result.emotion },
          ],
        }));
      }
    }, 1500 + Math.random() * 800);
  },

  // ─── PRESSURE ───
  doPressure: () => {
    const state = get();
    if (!state.playerCanAct || state.roundProcessing) return;

    const pressureLines = [
      "I need an answer NOW. I can't wait any longer.",
      "Let me be direct — this deal won't be here tomorrow.",
      "I think you know this is fair. Let's close this.",
      "Every minute we waste costs both of us money.",
    ];

    set({
      playerCanAct: false,
      roundProcessing: true,
      pressuresUsed: state.pressuresUsed + 1,
      tensionLevel: Math.min(1, state.tensionLevel + 0.1),
      chatLog: [
        ...state.chatLog,
        {
          sender: 'player',
          text: pressureLines[Math.floor(Math.random() * pressureLines.length)],
          type: 'pressure',
        },
      ],
    });

    setTimeout(() => set({ opponentTyping: true }), 400);

    setTimeout(() => {
      const s = get();
      const result = handlePressure({
        personality: s.personality,
        tensionLevel: s.tensionLevel,
      });

      let newChatEntry = {
        sender: 'opponent',
        text: result.message,
        emotion: result.emotion,
      };

      if (result.effective) {
        const scenario = s.scenario;
        const isBuyer = scenario.playerRole === 'buyer';
        const current = s.opponentLastOffer || scenario.opponentTarget;
        const concession = Math.abs(current - scenario.opponentFloor) * result.concessionBonus;
        const newPrice = isBuyer ? current - concession : current + concession;
        const rounded = roundPriceForScenario(newPrice, scenario);

        set(prev => ({
          opponentLastOffer: rounded,
          currentOffer: rounded,
          tensionLevel: Math.min(1, prev.tensionLevel + result.tensionIncrease),
          opponentMood: result.emotion,
          opponentTyping: false,
          playerCanAct: true,
          roundProcessing: false,
          chatLog: [...prev.chatLog, newChatEntry],
        }));
      } else {
        set(prev => ({
          tensionLevel: Math.min(1, prev.tensionLevel + result.tensionIncrease),
          opponentMood: result.emotion,
          opponentTyping: false,
          playerCanAct: true,
          roundProcessing: false,
          chatLog: [...prev.chatLog, newChatEntry],
        }));
      }
    }, 1200 + Math.random() * 600);
  },

  // ─── HOLD ───
  doHold: () => {
    const state = get();
    if (!state.playerCanAct || state.roundProcessing) return;

    set({
      playerCanAct: false,
      roundProcessing: true,
      tensionLevel: Math.min(1, state.tensionLevel + 0.04),
      chatLog: [
        ...state.chatLog,
        { sender: 'player', text: "I'm holding my position. Your move.", type: 'hold' },
      ],
    });

    setTimeout(() => set({ opponentTyping: true }), 500);

    setTimeout(() => {
      const s = get();
      const result = calculateOpponentAction({
        personality: s.personality,
        scenario: s.scenario,
        currentOffer: s.currentOffer,
        opponentLastOffer: s.opponentLastOffer,
        round: s.round,
        timeRemaining: s.timeRemaining,
        timeLimit: s.timeLimit,
        playerLastAction: 'hold',
        playerBluffCount: s.playerBluffCount,
        tensionLevel: s.tensionLevel,
      });

      if (result.action === 'accept') {
        set(prev => ({
          chatLog: [...prev.chatLog, { sender: 'opponent', text: result.message, emotion: result.emotion }],
          opponentTyping: false,
        }));
        setTimeout(() => get().endNegotiation(true, false, false, s.currentOffer), 800);
        return;
      }

      set(prev => ({
        opponentLastOffer: result.newPrice || prev.opponentLastOffer,
        currentOffer: result.newPrice || prev.currentOffer,
        opponentMood: result.emotion,
        opponentTyping: false,
        playerCanAct: true,
        roundProcessing: false,
        chatLog: [...prev.chatLog, { sender: 'opponent', text: result.message, emotion: result.emotion }],
      }));
    }, 1500 + Math.random() * 1000);
  },

  // ─── WALK AWAY ───
  doWalkAway: () => {
    const state = get();
    if (!state.playerCanAct) return;

    set({
      playerCanAct: false,
      chatLog: [
        ...state.chatLog,
        { sender: 'player', text: "I'm walking away. This isn't going to work.", type: 'walkaway' },
      ],
    });

    // Opponent might panic and concede (based on personality)
    const p = state.personalityData;
    const panicChance = (1 - p.riskTolerance) * 0.5 + state.tensionLevel * 0.2;

    setTimeout(() => {
      if (Math.random() < panicChance && state.round > 1) {
        const scenario = state.scenario;
        const isBuyer = scenario.playerRole === 'buyer';
        const floor = scenario.opponentFloor;
        const current = state.opponentLastOffer || scenario.opponentTarget;
        const panicPrice = isBuyer
          ? current - Math.abs(current - floor) * 0.4
          : current + Math.abs(floor - current) * 0.4;
        const rounded = roundPriceForScenario(panicPrice, scenario);

        set(prev => ({
          chatLog: [
            ...prev.chatLog,
            {
              sender: 'opponent',
              text: `Wait! Don't go. I can do ${formatPrice(rounded, scenario)}. Please.`,
              emotion: 'desperate',
            },
          ],
          opponentLastOffer: rounded,
          currentOffer: rounded,
          opponentMood: 'desperate',
          playerCanAct: true,
          roundProcessing: false,
        }));
      } else {
        get().endNegotiation(false, false, false);
      }
    }, 1000);
  },

  // ─── ACCEPT CURRENT ───
  acceptOffer: () => {
    const state = get();
    if (!state.playerCanAct) return;

    const price = state.opponentLastOffer || state.currentOffer;
    set({
      playerCanAct: false,
      chatLog: [
        ...state.chatLog,
        { sender: 'player', text: `Deal. ${formatPrice(price, state.scenario)}.`, type: 'accept' },
      ],
    });

    setTimeout(() => get().endNegotiation(true, false, false, price), 500);
  },

  // ─── END ───
  endNegotiation: (dealReached, opponentWalked, timedOut, finalPrice) => {
    const state = get();

    const result = {
      finalPrice: finalPrice || state.currentOffer,
      scenario: state.scenario,
      playerRole: state.playerRole,
      timeUsed: state.timeLimit - state.timeRemaining,
      timeLimit: state.timeLimit,
      totalRounds: state.round,
      bluffsUsed: state.playerBluffCount,
      bluffSuccesses: state.bluffSuccesses,
      pressuresUsed: state.pressuresUsed,
      dealReached,
      opponentWalkedAway: opponentWalked,
      playerWalkedAway: !dealReached && !opponentWalked && !timedOut,
    };

    const score = calculateScore(result);
    const streak = getStreakBonus(dealReached ? state.winStreak + 1 : 0);
    const adjustedScore = Math.round(score.total * streak.multiplier);

    // System message for end
    let endMsg;
    if (timedOut) endMsg = "⏰ Time's up! The negotiation ended without a deal.";
    else if (opponentWalked) endMsg = "🚪 The opponent walked away.";
    else if (!dealReached) endMsg = "🚶 You walked away from the deal.";
    else endMsg = `🤝 Deal closed at ${formatPrice(finalPrice, state.scenario)}!`;

    set({
      timerActive: false,
      dealReached,
      finalPrice: finalPrice || state.currentOffer,
      lastScore: { ...score, total: adjustedScore, streakBonus: streak },
      screen: 'results',
      playerCanAct: false,
      roundProcessing: false,

      // Career updates
      careerScore: state.careerScore + adjustedScore,
      dealsCompleted: state.dealsCompleted + (dealReached ? 1 : 0),
      dealsWalked: state.dealsWalked + (dealReached ? 0 : 1),
      winStreak: dealReached ? state.winStreak + 1 : 0,
      bestStreak: Math.max(state.bestStreak, dealReached ? state.winStreak + 1 : state.bestStreak),
      totalRoundsPlayed: state.totalRoundsPlayed + state.round,
      scenariosPlayed: [...state.scenariosPlayed, state.scenario.id],
      careerHistory: [
        ...state.careerHistory,
        {
          scenario: state.scenario.title,
          opponent: state.opponentName,
          personality: state.personality,
          dealReached,
          finalPrice: finalPrice || state.currentOffer,
          score: adjustedScore,
          grade: score.grade,
        },
      ],

      chatLog: [
        ...state.chatLog,
        { sender: 'system', text: endMsg, type: 'end' },
      ],
    });
  },

  // ─── RESET ───
  resetGame: () => set({ ...INITIAL_STATE }),
}));

function roundPriceForScenario(price, scenario) {
  if (scenario.isPercentage) return Math.round(price * 10) / 10;
  if (Math.abs(scenario.listPrice) >= 1e6) return Math.round(price / 10000) * 10000;
  if (Math.abs(scenario.listPrice) >= 10000) return Math.round(price / 100) * 100;
  if (Math.abs(scenario.listPrice) >= 100) return Math.round(price / 5) * 5;
  return Math.round(price * 100) / 100;
}

export default useGameStore;
